import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedSection from "../../WebDevelopmentPage/components/AnimatedSection";
import { uerpModules, ModuleFeature } from "../uerpModulesData";
import { uerpFeatureIconMap } from "../uerpModuleIcons";

const FeatureBlock: React.FC<{ feature: ModuleFeature }> = ({ feature }) => {
  const Icon = uerpFeatureIconMap[feature.icon];

  return (
    <div className="hms-module-feature uerp-module-feature">
      <div className="uerp-module-feature__head">
        <span className="uerp-module-feature__icon" aria-hidden="true">
          <Icon size={18} strokeWidth={1.75} />
        </span>
        <h4 className="hms-module-feature__title uerp-module-feature__title">{feature.title}</h4>
      </div>
      {feature.description && <p className="hms-module-feature__desc">{feature.description}</p>}
      {feature.items && (
        <ul className="hms-module-feature__list">
          {feature.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

const UerpModulesSection: React.FC = () => {
  const [activeId, setActiveId] = useState(uerpModules[0].id);
  const activeModule = uerpModules.find((m) => m.id === activeId) ?? uerpModules[0];

  const tabLabel = (title: string) =>
    title.replace(" Portal", "").replace(" Module", "").replace("Library & Hostel", "Library & Hostel");

  return (
    <section className="hms-modules" aria-labelledby="uerp-modules-title">
      <div className="container">
        <AnimatedSection className="wdev-section-head wdev-section-head--center">
          <h2
            id="uerp-modules-title"
            className="wdev-section-head__title wdev-section-head__title--dark wdev-section-head__title--underline"
          >
            ERP Modules
          </h2>
          <p className="hms-modules__lead">
            Six integrated modules covering student life, faculty operations, administration,
            examinations, finance, and campus facilities.
          </p>
        </AnimatedSection>

        <div className="hms-modules__tabs uerp-modules__tabs" role="tablist" aria-label="University ERP modules">
          {uerpModules.map((mod) => (
            <button
              key={mod.id}
              type="button"
              role="tab"
              aria-selected={activeId === mod.id}
              aria-controls={`uerp-panel-${mod.id}`}
              id={`uerp-tab-${mod.id}`}
              className={`hms-modules__tab ${activeId === mod.id ? "hms-modules__tab--active" : ""}`}
              onClick={() => setActiveId(mod.id)}
            >
              {tabLabel(mod.title)}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeModule.id}
            id={`uerp-panel-${activeModule.id}`}
            role="tabpanel"
            aria-labelledby={`uerp-tab-${activeModule.id}`}
            className="hms-modules__panel"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            <div className="hms-modules__panel-head">
              <h3>{activeModule.title}</h3>
              <p>{activeModule.description}</p>
            </div>

            <div className="hms-modules__features">
              {activeModule.features.map((feature) => (
                <FeatureBlock key={feature.title} feature={feature} />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default UerpModulesSection;
